import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Copies content, including newlines and **bold** markdown, to the clipboard 
 * with proper HTML formatting for rich text applications.
 * 
 * @param rawContent - The raw string content to copy, potentially containing newlines and markdown.
 * @param options - Options for the copy operation (onSuccess, onError).
 * @returns Promise resolving to true if copy succeeded, false otherwise.
 */
export const copyFormattedContent = async (
  rawContent: string, 
  options: { 
    onSuccess?: () => void, 
    onError?: (error: Error) => void 
  } = {}
): Promise<boolean> => {
  if (!rawContent) {
    const error = new Error('No content provided to copy');
    console.error(error.message);
    if (options.onError) options.onError(error);
    return false;
  }

  let formattedHtml: string;
  let plainText: string;

  try {
    // --- Formatting Start ---
    // 1. Clean input: Remove any pre-existing HTML tags for safety.
    let cleanContent = rawContent.replace(/<\/?[^>]+(>|$)/g, "");

    // 2. Convert newlines to <br> tags for HTML.
    let contentWithBreaks = cleanContent.replace(/\n/g, '<br />');

    // 3. Convert markdown **bold** to <strong> tags.
    const boldRegex = /\*\*([^*]+)\*\*/g;
    formattedHtml = contentWithBreaks.replace(boldRegex, '<strong>$1</strong>');

    // 4. Derive plain text version *after* formatting for fallback.
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = formattedHtml;
    plainText = tempDiv.textContent || tempDiv.innerText || cleanContent; // Fallback
    // --- Formatting End ---

  } catch (formatError) {
    // Handle errors during the formatting stage itself
    const error = new Error('Failed to format content for copying.');
    console.error(error.message, formatError);
    if (options.onError) options.onError(error);
    return false;
  }
  
  // --- Clipboard Operations Start ---
  try {
    // 1. Try modern Clipboard API with HTML and Plain Text
    if (
      navigator.clipboard && 
      typeof navigator.clipboard.write === 'function' && 
      typeof window.ClipboardItem === 'function'
    ) {
      try {
        // @ts-ignore - ClipboardItem might not be recognized in all TS envs
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([formattedHtml], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' })
        });
        
        await navigator.clipboard.write([clipboardItem]);
        console.log('Formatted content copied using modern Clipboard API.');
        if (options.onSuccess) options.onSuccess();
        return true;
      } catch (err) {
        console.warn('Modern Clipboard API (HTML) failed: ', err);
        // Fall through to the next method
      }
    }

    // 2. Fallback: Try using document.execCommand('copy') - less reliable for HTML
    console.warn('Falling back to document.execCommand("copy"). HTML formatting might be lost.');
    const tempElem = document.createElement('div');
    Object.assign(tempElem.style, { position: 'absolute', left: '-9999px', top: '0', opacity: '0' });
    tempElem.innerHTML = formattedHtml; // Use the formatted HTML
    document.body.appendChild(tempElem);
    
    let success = false;
    try {
      const range = document.createRange();
      range.selectNodeContents(tempElem);
      const selection = window.getSelection();
      if (!selection) throw new Error('Unable to access window selection');
      selection.removeAllRanges();
      selection.addRange(range);
      success = document.execCommand('copy');
      selection.removeAllRanges();
      if (success) {
        console.log('Content copied using execCommand (formatting might be partial).');
      } else {
        console.warn('document.execCommand("copy") returned false.');
      }
    } catch (execErr) {
      console.warn('Error during document.execCommand("copy"):', execErr);
      success = false;
    } finally {
      document.body.removeChild(tempElem);
    }
    
    if (success) {
      if (options.onSuccess) options.onSuccess();
      return true;
    }

    // 3. Last Resort: Try copying plain text using modern API
    console.warn('Falling back to navigator.clipboard.writeText (plain text only).');
    try {
      await navigator.clipboard.writeText(plainText); // Use derived plain text
      console.log('Content copied as plain text using writeText.');
      // Consider if this should be reported as full success if formatting was the goal
      if (options.onSuccess) options.onSuccess(); 
      return true;
    } catch (writeTextErr) {
      const finalError = new Error('All clipboard methods failed, including plain text fallback.');
      console.error(finalError.message, writeTextErr);
      if (options.onError) options.onError(finalError);
      return false;
    }

  } catch (clipboardError) {
    // Catch errors from the clipboard operations phase
    const unexpectedError = new Error('Unexpected error during clipboard operations.');
    console.error(unexpectedError.message, clipboardError);
    if (options.onError) options.onError(unexpectedError);
    return false;
  }
  // --- Clipboard Operations End ---
};
